import { useEffect, useRef, useState } from "react";

/**
 * Dynamic cursor:
 *  - inner dot follows cursor exactly
 *  - outer ring trails with lerp easing
 *  - ring scales up + fills with gold tint on hover over interactive elements
 *  - short-lived gold dust particles spawn on movement
 * Hidden on touch / coarse pointers.
 */
export default function CursorDynamic() {
  const ringRef = useRef(null);
  const dotRef = useRef(null);
  const trailCanvasRef = useRef(null);
  const [enabled] = useState(() => typeof window !== "undefined" && window.matchMedia("(pointer: fine)").matches);

  useEffect(() => {
    if (!enabled) return;

    let x = window.innerWidth / 2;
    let y = window.innerHeight / 2;
    let rx = x, ry = y;
    let hovering = false;
    let clicking = false;

    // -------- trail particles --------
    const canvas = trailCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d", { alpha: true });
    const dpr = window.devicePixelRatio || 1;
    const resize = () => {
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      canvas.style.width = window.innerWidth + "px";
      canvas.style.height = window.innerHeight + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const particles = [];
    let lastSpawn = 0;

    const spawn = (px, py) => {
      for (let i = 0; i < 2; i++) {
        particles.push({
          x: px + (Math.random() - 0.5) * 6,
          y: py + (Math.random() - 0.5) * 6,
          vx: (Math.random() - 0.5) * 0.6,
          vy: (Math.random() - 0.2) * 0.6,
          r: Math.random() * 1.6 + 0.6,
          life: 0,
          ttl: 40 + Math.random() * 30,
        });
      }
      if (particles.length > 120) particles.splice(0, particles.length - 120);
    };

    // -------- mouse handlers --------
    const isInteractive = (el) => {
      if (!el || el.nodeType !== 1) return false;
      const tag = el.tagName;
      if (tag === "A" || tag === "BUTTON" || tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || tag === "LABEL") return true;
      if (el.getAttribute && (el.getAttribute("role") === "button" || el.hasAttribute("data-testid"))) {
        if (el.matches && el.matches('button, a, [role="button"]')) return true;
      }
      return el.closest && !!el.closest('a, button, [role="button"], input, textarea, select, label');
    };

    const onMove = (e) => {
      x = e.clientX;
      y = e.clientY;
      if (dotRef.current) dotRef.current.style.transform = `translate3d(${x - 3}px, ${y - 3}px, 0)`;
      const now = performance.now();
      if (now - lastSpawn > 22) {
        spawn(x, y);
        lastSpawn = now;
      }
      const target = e.target;
      const nextHover = isInteractive(target);
      if (nextHover !== hovering) {
        hovering = nextHover;
        if (ringRef.current) ringRef.current.dataset.hover = hovering ? "1" : "0";
      }
    };

    const onDown = () => {
      clicking = true;
      if (ringRef.current) ringRef.current.dataset.click = "1";
    };
    const onUp = () => {
      clicking = false;
      if (ringRef.current) ringRef.current.dataset.click = "0";
    };
    const onLeave = () => {
      if (ringRef.current) ringRef.current.style.opacity = "0";
      if (dotRef.current) dotRef.current.style.opacity = "0";
    };
    const onEnter = () => {
      if (ringRef.current) ringRef.current.style.opacity = "1";
      if (dotRef.current) dotRef.current.style.opacity = "1";
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mousedown", onDown);
    window.addEventListener("mouseup", onUp);
    document.addEventListener("mouseleave", onLeave);
    document.addEventListener("mouseenter", onEnter);

    // -------- render loop --------
    let raf = 0;
    const tick = () => {
      rx += (x - rx) * 0.2;
      ry += (y - ry) * 0.2;
      if (ringRef.current) {
        const size = hovering ? 56 : 32;
        const half = size / 2;
        ringRef.current.style.width = size + "px";
        ringRef.current.style.height = size + "px";
        ringRef.current.style.transform = `translate3d(${rx - half}px, ${ry - half}px, 0) scale(${clicking ? 0.85 : 1})`;
      }

      // particles
      ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);
      for (let i = particles.length - 1; i >= 0; i--) {
        const p = particles[i];
        p.life++;
        p.x += p.vx;
        p.y += p.vy;
        p.vy -= 0.008; // slight upward drift
        const t = p.life / p.ttl;
        if (t >= 1) { particles.splice(i, 1); continue; }
        const alpha = (1 - t) * 0.9;
        const rad = p.r * (1 + t * 0.8);
        const grd = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, rad * 3);
        grd.addColorStop(0, `rgba(243, 229, 171, ${alpha})`);
        grd.addColorStop(0.5, `rgba(212, 175, 55, ${alpha * 0.5})`);
        grd.addColorStop(1, "rgba(212, 175, 55, 0)");
        ctx.fillStyle = grd;
        ctx.beginPath();
        ctx.arc(p.x, p.y, rad * 3, 0, Math.PI * 2);
        ctx.fill();
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("resize", resize);
      document.removeEventListener("mouseleave", onLeave);
      document.removeEventListener("mouseenter", onEnter);
    };
  }, []);

  if (!enabled) return null;
  return (
    <>
      <canvas
        ref={trailCanvasRef}
        aria-hidden
        className="pointer-events-none fixed inset-0 z-[88] mix-blend-screen"
      />
      <div
        ref={ringRef}
        aria-hidden
        data-hover="0"
        data-click="0"
        className="cursor-ring pointer-events-none fixed top-0 left-0 z-[90] rounded-full border border-[#B8873B]/85 transition-[width,height,background-color,border-color,transform] duration-200 ease-out"
        style={{
          width: 32,
          height: 32,
          boxShadow: "0 0 22px rgba(212,175,55,0.35), inset 0 0 10px rgba(212,175,55,0.15)",
        }}
      />
      <div
        ref={dotRef}
        aria-hidden
        className="pointer-events-none fixed top-0 left-0 z-[91] w-1.5 h-1.5 rounded-full bg-[#E6C88A]"
        style={{ boxShadow: "0 0 10px #B8873B" }}
      />
      <style>{`
        @media (pointer: fine) {
          html, body { cursor: none; }
          a, button, input, textarea, select, label, [role="button"] { cursor: none; }
        }
        .cursor-ring[data-hover="1"] {
          background: rgba(212, 175, 55, 0.14);
          border-color: #E6C88A;
          box-shadow: 0 0 32px rgba(212,175,55,0.55), inset 0 0 18px rgba(212,175,55,0.25);
        }
      `}</style>
    </>
  );
}
